import { forwardRef } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FILTER_SEPARATOR } from "go-list/core/components/Filter/services";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ReactComponent as CallAnsweredSVG } from "../../../../images/svg/dashboard/phone-answered.svg";
import { ReactComponent as CallMissedSvg } from "../../../../images/svg/dashboard/phone-missed.svg";
import useDashboardContext from "../../services/hooks/useDashboardContext";
import { useDashboardDraggable } from "../../services/hooks/useDashboardDraggable";
import { DashboardGridItem, DraggableComponentProps } from "../../services/types";
import { getCallRecorderData, getSeeMoreLink } from "../../utils/utils";
import DashboardCardItem from "../Common/DashboardCardItem";

export const DraggableDashboardCallRecorder = forwardRef<HTMLDivElement, DraggableComponentProps>(
	({ style, className, onMouseDown, onMouseUp, onTouchEnd, setNonMobileLayout }, ref) => {
		const { t } = useTranslation();
		const {
			isEditMode,
			state: {
				data,
				filtersConfig: {
					selectedFilter: { dateRange },
				},
			},
		} = useDashboardContext();
		const refChildren = ref && "current" in ref ? ref.current?.children?.[0]?.children : undefined;
		const { gridItemZIndex, cursorType, onMouseEnter, onMouseLeave, onCustomMouseDown, onCustomMouseUp } =
			useDashboardDraggable(
				DashboardGridItem.CALL_RECORDER,
				setNonMobileLayout,
				refChildren,
				onMouseDown,
				onMouseUp
			);
		const organization = useSelector(selectOrganization);
		const { answeredCalls, missedCalls, previousPeriodAnsweredCalls, previousPeriodMissedCalls } =
			getCallRecorderData(data?.callsReportCurrentDateRange, data?.callsReportPreviousDateRange);
		const answeredCallConfig = {
			title: t("modules.dashboard.field.answered_calls.title"),
			icon: <CallAnsweredSVG />,
			value: answeredCalls,
			compareValue: previousPeriodAnsweredCalls,
			seeMoreLink: getSeeMoreLink({
				dateRange,
				reportName: "calls",
				organizationId: organization.id,
				customFilters: `${FILTER_SEPARATOR}status|e=RECEIVED`,
				customGroups: "STATUS",
			}),
		};
		const missedCallConfig = {
			title: t("modules.dashboard.field.missed_calls.title"),
			icon: <CallMissedSvg />,
			value: missedCalls,
			compareValue: previousPeriodMissedCalls,
			seeMoreLink: getSeeMoreLink({
				dateRange,
				reportName: "calls",
				organizationId: organization.id,
				customFilters: `${FILTER_SEPARATOR}status|e=MISSED`,
				customGroups: "STATUS",
			}),
		};

		return (
			<Card
				className={`${className} ${
					isEditMode ? "dashboard-edit-mode-element-active" : ""
				}  dashboard-non-chart-card`}
				style={{ ...style, zIndex: gridItemZIndex, cursor: cursorType }}
				onMouseDown={onCustomMouseDown}
				onMouseUp={onCustomMouseUp}
				onTouchEnd={onTouchEnd}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				ref={ref}
			>
				<Card.Body>
					<h5>{t("modules.app_link.header.title")}</h5>
					<div className="dashboard-non-chart-card-items-wrapper">
						<DashboardCardItem config={answeredCallConfig} />
						<DashboardCardItem config={missedCallConfig} />
					</div>
				</Card.Body>
			</Card>
		);
	}
);

DraggableDashboardCallRecorder.displayName = "DraggableDashboardCallRecorder";
